import React, { useState, useEffect, useRef } from 'react';
import minutesImg from '../assets/image/minutes transcribed.png';
import filesImg from '../assets/image/files processed.png';
import hoursImg from '../assets/image/hours saved.png';

const StatsCounter = ({ showHeading = false, title = '', subtitle = '', isHeroVersion = false }) => {
  const [fetched, setFetched] = useState({
    minutes_transcribed: 0,
    files_processed: 0,
    hours_saved: 0,
  });

  // displayed values that animate
  const [display, setDisplay] = useState({
    minutes_transcribed: 0,
    files_processed: 0,
    hours_saved: 0,
  });

  const mounted = useRef(true);

  // Fetch stats from API
  const fetchStats = async () => {
    try {
      const response = await fetch('https://audiosum.app/api/stats');
      if (!response.ok) return;
      const data = await response.json();
      if (!mounted.current) return;
      // ensure numbers
      setFetched({
        minutes_transcribed: Number(data.minutes_transcribed) || 0,
        files_processed: Number(data.files_processed) || 0,
        hours_saved: Number(data.hours_saved) || 0,
      });
    } catch (error) {
      // swallow, keep defaults
      // console.error('Error fetching stats:', error);
    }
  };

  // Animate single value from start to end
  const animateSingle = (start, end, duration, setter) => {
    const range = end - start;
    if (range === 0) {
      setter(end);
      return;
    }
    const stepTime = 16; // ~60fps
    const steps = Math.max(Math.round(duration / stepTime), 1);
    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep += 1;
      const progress = currentStep / steps;
      const value = Math.round(start + range * progress);
      setter(value);
      if (currentStep >= steps) {
        clearInterval(timer);
      }
    }, stepTime);
  };

  useEffect(() => {
    mounted.current = true;
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => {
      mounted.current = false;
      clearInterval(interval);
    };
  }, []);

  // When fetched values change, animate display values toward them
  useEffect(() => {
    animateSingle(display.minutes_transcribed, fetched.minutes_transcribed, 1500, (val) => {
      setDisplay((p) => ({ ...p, minutes_transcribed: val }));
    });
    animateSingle(display.files_processed, fetched.files_processed, 1500, (val) => {
      setDisplay((p) => ({ ...p, files_processed: val }));
    });
    animateSingle(display.hours_saved, fetched.hours_saved, 1500, (val) => {
      setDisplay((p) => ({ ...p, hours_saved: val }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetched.minutes_transcribed, fetched.files_processed, fetched.hours_saved]);

  return (
    <div style={isHeroVersion ? styles.heroContainer : styles.container} className="stats-cards-root">
      {showHeading && (
        <div style={styles.headingContainer} data-aos="zoom-in">
          <h2 style={styles.heading}>{title}</h2>
          <p style={styles.subheading}>{subtitle}</p>
        </div>
      )}
      
      <div style={styles.cardsContainer} className="stats-cards-container">
        <div style={{...styles.card, ...styles.card1}} data-aos="zoom-in" data-aos-delay="100">
          <img src={minutesImg} alt="Minutes Transcribed" style={styles.icon} className="stat-icon-pulsate" />
          <div style={styles.number}>{display.minutes_transcribed.toLocaleString()}</div>
          <div style={styles.label}>Minutes Transcribed</div>
        </div>

        <div style={{...styles.card, ...styles.card2}} data-aos="zoom-in" data-aos-delay="200">
          <img src={filesImg} alt="Files Processed" style={styles.icon} className="stat-icon-pulsate" />
          <div style={styles.number}>{display.files_processed.toLocaleString()}</div>
          <div style={styles.label}>Files Processed</div>
        </div>

        <div style={{...styles.card, ...styles.card3}} data-aos="zoom-in" data-aos-delay="300">
          <img src={hoursImg} alt="Hours Saved" style={styles.icon} className="stat-icon-pulsate" />
          <div style={styles.number}>{display.hours_saved.toLocaleString()}</div>
          <div style={styles.label}>Hours Saved</div>
        </div>
      </div>

      <style>{`
        @keyframes pulsate {
          0%, 100% {
            transform: scale(1);
            filter: drop-shadow(0 0 20px rgba(59, 130, 246, 0.5));
          }
          50% {
            transform: scale(1.15);
            filter: drop-shadow(0 0 30px rgba(96, 165, 250, 0.8));
          }
        }

        .stat-icon-pulsate {
          animation: pulsate 2.5s ease-in-out infinite;
        }

        @media (max-width: 768px) {
          .stats-cards-container {
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 1.5rem;
            padding: 0 1rem;
          }
          
          .stats-cards-container > div {
            width: 100%;
            max-width: 320px;
            margin: 0 auto;
          }
          
          .stats-cards-root {
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  heroContainer: {
    width: '100%',
    padding: '4rem 1rem 2rem',
    backgroundColor: 'transparent',
    marginTop: '3rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  container: {
    width: '100%',
    padding: '2rem 1rem 12rem',
    backgroundColor: 'transparent',
    marginTop: '0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  headingContainer: {
    textAlign: 'center',
    marginBottom: '3rem',
    maxWidth: '800px',
  },
  heading: {
    fontSize: '2.5rem',
    fontWeight: '800',
    color: '#ffffff',
    marginBottom: '1rem',
    lineHeight: '1.2',
  },
  subheading: {
    fontSize: '1.2rem',
    color: '#94a3b8',
    fontWeight: '400',
    lineHeight: '1.6',
  },
  cardsContainer: {
    display: 'flex',
    gap: '1.5rem',
    justifyContent: 'center',
    alignItems: 'stretch',
    flexWrap: 'wrap',
    maxWidth: '1000px',
    margin: '0 auto',
    width: '100%',
  },
  card: {
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '18px',
    padding: '2rem 1.5rem',
    textAlign: 'center',
    position: 'relative',
    transition: 'all 0.3s ease',
    width: '100%',
    maxWidth: '280px',
    cursor: 'default',
  },
  card1: {
    background: 'rgba(255, 255, 255, 0.05)',
    border: '2px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
  },
  card2: {
    background: 'rgba(255, 255, 255, 0.05)',
    border: '2px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
  },
  card3: {
    background: 'rgba(255, 255, 255, 0.05)',
    border: '2px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
  },
  icon: {
    width: '56px',
    height: '56px',
    marginBottom: '1rem',
  },
  number: {
    fontSize: '2.5rem',
    fontWeight: '900',
    color: '#ffffff',
    marginBottom: '0.5rem',
    lineHeight: '1',
  },
  label: {
    color: '#94a3b8',
    fontSize: '0.9rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
};

export default StatsCounter;
